import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export function ResetPasswordModal({ token, onDone }) {
  const { resetPassword, formatApiErrorDetail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const password = (fd.get("password") ?? form.password ?? "").toString();
    const confirm = (fd.get("confirm") ?? form.confirm ?? "").toString();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const u = await resetPassword(token, password);
      toast.success(`Password updated. Welcome back, ${u.name}.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onDone(); }}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200" data-testid="reset-password-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-lg bg-navy flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-persian-blue" />
            </div>
            <DialogTitle className="font-serif text-2xl text-navy">Choose a new password</DialogTitle>
          </div>
          <DialogDescription className="text-slate-500">
            Set a new password for your OCC account. You'll be signed in automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="rp-pass">New password</Label>
            <Input id="rp-pass" type="password" name="password" autoComplete="new-password" required minLength={6}
              value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              data-testid="reset-password-input" placeholder="At least 6 characters" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rp-confirm">Confirm password</Label>
            <Input id="rp-confirm" type="password" name="confirm" autoComplete="new-password" required minLength={6}
              value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
              data-testid="reset-confirm-input" placeholder="Re-enter password" />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit-button"
            className="w-full bg-navy hover:bg-navy-light text-white">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Update password & sign in
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
